import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import Table from "../../components/table/ReactTable/Table";
import { ColumnFilter } from "../../components/table/ReactTable/ColumnFilter";
import { useSelector, useDispatch } from "react-redux";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import { inventoryHistoryActions } from "../../../_store";
import generatePDF from "../../components/export/generatePDF";
import generateXlsx from "../../components/export/generateXlsx";

const MRDiscrepancyReport = () => {
  const dispatch = useDispatch();

  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // ------------------ Reducer --------------------
  const MRDiscrepancyListReport = useSelector(
    (x) => x.inventoryHistory.MRDiscrepancyListReport,
  );

  // ------------------ Table Data --------------------
  const [finalTableData, setFinalTableData] = useState([]);
  const handleTableDataUpdate = useCallback((data) => {
    setFinalTableData(data);
  }, []);

  useEffect(() => {
    dispatch(inventoryHistoryActions.getMRDiscrepancyReport());
  }, [dispatch]);

  // ------------------ Columns --------------------
  const COLUMNS = [
    {
      Header: "Purchase Order #",
      Footer: "Purchase Order #",
      accessor: "PurchaseOrderNumber",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Invoice Number",
      Footer: "Invoice Number",
      accessor: "InvoiceNumber",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Vendor",
      Footer: "Vendor",
      accessor: "VendorName",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "SKU",
      Footer: "SKU",
      accessor: "SKU",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Product Name",
      Footer: "Product Name",
      accessor: "ProductName",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Ordered Units",
      Footer: "Ordered Units",
      accessor: "OrderUnits",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Processed Units",
      Footer: "Processed Units",
      accessor: "OrderProcessedUnits",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Difference",
      Footer: "Difference",
      accessor: "Difference",
      justify: "center",
      Filter: ColumnFilter,
      Cell: ({ value }) => {
        let badgeClass = "";

        if (value > 0) {
          badgeClass = "badge badge-outline-success";
        } else if (value < 0) {
          badgeClass = "badge badge-outline-danger";
        } else {
          badgeClass = "badge badge-outline-secondary";
        }

        return <span className={`badge-rounded ${badgeClass}`}>{value}</span>;
      },
    },
  ];

  // ------------------ PDF Columns --------------------
  const generateColumns = [
    { header: "Purchase Order #", dataKey: "PurchaseOrderNumber" },
    { header: "Invoice Number", dataKey: "InvoiceNumber" },
    { header: "Vendor", dataKey: "VendorName" },
    { header: "SKU", dataKey: "SKU" },
    { header: "Product Name", dataKey: "ProductName" },
    { header: "Ordered Units", dataKey: "OrderUnits" },
    { header: "Processed Units", dataKey: "OrderProcessedUnits" },
    { header: "Difference", dataKey: "Difference" },
  ];

  // ------------------ Excel Columns --------------------
  const generateExcelColumns = [
    "Purchase Order #",
    "Invoice Number",
    "Vendor",
    "SKU",
    "Product Name",
    "Ordered Units",
    "Processed Units",
    "Difference",
  ];

  // ------------------ PDF Styles --------------------
  const columnStyles = {
    PurchaseOrderNumber: { halign: "center", cellWidth: 40 },
    InvoiceNumber: { halign: "center", cellWidth: 40 },
    VendorName: { halign: "center", cellWidth: 35 },
    SKU: { halign: "center", cellWidth: 30 },
    ProductName: { halign: "center", cellWidth: 40 },
    OrderUnits: { halign: "center", cellWidth: 30 },
    OrderProcessedUnits: { halign: "center", cellWidth: 30 },
    Difference: { halign: "center", cellWidth: 25 },
  };

  // ------------------ Excel Mapping --------------------
  let new_MRDiscrepancyListReport =
    finalTableData &&
    finalTableData.map((obj) => {
      return {
        PurchaseOrderNumber: obj.PurchaseOrderNumber,
        InvoiceNumber: obj.InvoiceNumber,
        VendorName: obj.VendorName,
        SKU: obj.SKU,
        ProductName: obj.ProductName,
        OrderUnits: obj.OrderUnits,
        OrderProcessedUnits: obj.OrderProcessedUnits,
        Difference: obj.Difference,
      };
    });

  // ------------------ PDF Download --------------------
  const handleClickPDF = () => {
    let title = "Merchandise Reception Discrepancy Report";

    generatePDF(finalTableData, generateColumns, true, title, columnStyles);
  };

  // ------------------ Excel Download --------------------
  const handleClickExcel = () => {
    let title = "Merchandise Reception Discrepancy Report";
    generateXlsx(new_MRDiscrepancyListReport, generateExcelColumns, title);
  };

  const columns = useMemo(() => COLUMNS, []);

  return (
    <>
      <PageTitle
        activeMenu="Merchandise Reception Discrepancy Report"
        motherMenu="Inventory"
      />
      <ToastContainer />

      <div className="card">
        <div className="card-header">
          <h4 className="card-title header-title">
            Merchandise Reception Discrepancy Report
          </h4>

          <div className="float-sm-end">
            <Button
              className="me-2"
              variant="danger btn-rounded"
              onClick={handleClickPDF}
            >
              <span className="btn-icon-start text-danger">
                <i className="fa fa-solid fa-file-pdf"></i>
              </span>
              PDF
            </Button>

            <Button
              className="me-2"
              variant="secondary btn-rounded"
              onClick={handleClickExcel}
            >
              <span className="btn-icon-start text-secondary">
                <i className="fa fa-regular fa-file-excel"></i>
              </span>
              Excel
            </Button>
          </div>
        </div>

        <div className="card-body">
          <Table
            columns={columns}
            data={MRDiscrepancyListReport}
            onDataUpdate={handleTableDataUpdate}
          />
        </div>
      </div>

      {loader}
    </>
  );
};

export default MRDiscrepancyReport;
